import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  applySuperAdminPlatformTemplateSeason,
  deleteSuperAdminPlatformTemplate,
  getSuperAdminPlatformTemplateCloneStats,
  getSuperAdminPlatformTemplateSeasonCalendar,
  getSuperAdminPlatformTemplates,
  reorderSuperAdminPlatformTemplates,
  SaCloneStats,
  SaPlatformTemplate,
  SaSeasonCalendar,
} from "../../../helpers/super_admin_platform_templates_helper";
import { localizePlatformDescription } from "../../../helpers/platform_templates_helper";

const PER_PAGE = 50;
const SEARCH_DEBOUNCE_MS = 350;

type ActiveFilter = "" | "1" | "0";

/**
 * Listagem SA do catálogo guia + DnD de ordem + clone_count (DOC 02 § 8).
 */
const PlatformTemplatesList = () => {
  const { t, i18n } = useTranslation();
  document.title = t("sa.platformTemplates.documentTitle");

  const [items, setItems] = useState<SaPlatformTemplate[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("");
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<SaPlatformTemplate | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [cloneStats, setCloneStats] = useState<SaCloneStats | null>(null);
  const [seasonYear, setSeasonYear] = useState(new Date().getUTCFullYear());
  const [seasonCalendar, setSeasonCalendar] = useState<SaSeasonCalendar | null>(
    null
  );
  const [seasonBusyPack, setSeasonBusyPack] = useState<string | null>(null);

  const hasFilters = Boolean(appliedSearch) || activeFilter !== "";
  const canDrag = !hasFilters && page === 1 && lastPage === 1;

  useEffect(() => {
    getSuperAdminPlatformTemplateCloneStats(14)
      .then((res) => {
        if (res?.success) {
          setCloneStats(res.data);
        }
      })
      .catch(() => setCloneStats(null));
  }, []);

  const loadSeasonCalendar = useCallback(async (year: number) => {
    try {
      const res = await getSuperAdminPlatformTemplateSeasonCalendar(year);
      if (res?.success) {
        setSeasonCalendar(res.data);
      }
    } catch {
      setSeasonCalendar(null);
    }
  }, []);

  useEffect(() => {
    void loadSeasonCalendar(seasonYear);
  }, [seasonYear, loadSeasonCalendar]);

  const applySeasonPack = async (pack: string, clear = false) => {
    setSeasonBusyPack(pack);
    try {
      const res = await applySuperAdminPlatformTemplateSeason({
        pack,
        year: seasonYear,
        clear,
      });
      toast.success(
        t("sa.platformTemplates.toast.seasonApplied", {
          count: res?.data?.updated ?? 0,
        })
      );
      await loadSeasonCalendar(seasonYear);
      await load();
    } catch (err: any) {
      toast.error(err?.message || t("sa.platformTemplates.error.season"));
    } finally {
      setSeasonBusyPack(null);
    }
  };
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSuperAdminPlatformTemplates({
        search: appliedSearch || undefined,
        page,
        per_page: PER_PAGE,
        is_active:
          activeFilter === "" ? undefined : activeFilter === "1",
      });
      if (!res?.success) {
        setError(t("sa.platformTemplates.error.load"));
        setItems([]);
        return;
      }
      setItems(res.data || []);
      setTotal(res.meta?.total ?? 0);
      setLastPage(res.meta?.last_page ?? 1);
    } catch (err: any) {
      setError(err?.message || t("sa.platformTemplates.error.load"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, page, activeFilter, t]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = search.trim();
      setAppliedSearch((prev) => {
        if (prev === next) {
          return prev;
        }
        setPage(1);
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const clearFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setActiveFilter("");
    setPage(1);
  };

  const persistOrder = async (nextItems: SaPlatformTemplate[]) => {
    setReordering(true);
    setError(null);
    try {
      const res = await reorderSuperAdminPlatformTemplates(
        nextItems.map((item) => item.id)
      );
      toast.success(
        res?.message || t("sa.platformTemplates.toast.reordered"),
        { autoClose: 2500 }
      );
      setItems(
        nextItems.map((item, index) => ({
          ...item,
          sort_order: index * 10,
        }))
      );
    } catch (err: any) {
      setError(err?.message || t("sa.platformTemplates.error.reorder"));
      await load();
    } finally {
      setReordering(false);
    }
  };

  const onDropRow = async (toIndex: number) => {
    if (dragIndex === null || dragIndex === toIndex || !canDrag) {
      setDragIndex(null);
      return;
    }
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(toIndex, 0, moved);
    setDragIndex(null);
    setItems(next);
    await persistOrder(next);
  };

  const confirmDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeletingBusy(true);
    setError(null);
    try {
      const res = await deleteSuperAdminPlatformTemplate(deleting.id);
      toast.success(
        res?.message || t("sa.platformTemplates.toast.deleted"),
        { autoClose: 2500 }
      );
      setDeleting(null);
      await load();
    } catch (err: any) {
      setError(err?.message || t("sa.platformTemplates.error.delete"));
      setDeleting(null);
    } finally {
      setDeletingBusy(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.platformTemplates.title")}
          pageTitle={t("sa.platformTemplates.breadcrumb")}
        />
        <ToastContainer closeButton={false} limit={1} />

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        {cloneStats ? (
          <Card className="mb-3">
            <CardBody className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
              <div>
                <h5 className="mb-1">
                  {t("sa.platformTemplates.stats.title", {
                    days: cloneStats.days,
                  })}
                </h5>
                <p className="text-muted mb-0">
                  {t("sa.platformTemplates.stats.total", {
                    count: cloneStats.total,
                  })}
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2">
                {cloneStats.top.slice(0, 5).map((row) => (
                  <Badge key={row.id} color="light" className="text-body">
                    {row.name}: {row.count}
                  </Badge>
                ))}
              </div>
            </CardBody>
          </Card>
        ) : null}

        <Card className="mb-3">
          <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
            <div>
              <h5 className="mb-1">
                {t("sa.platformTemplates.season.title", { year: seasonYear })}
              </h5>
              <p className="text-muted mb-0 small">
                {t("sa.platformTemplates.season.hint")}
              </p>
            </div>
            <Input
              type="number"
              style={{ maxWidth: 110 }}
              value={seasonYear}
              aria-label={t("sa.platformTemplates.season.year")}
              onChange={(e) => {
                const next = Number(e.target.value);
                if (Number.isFinite(next) && next >= 2024 && next <= 2100) {
                  setSeasonYear(next);
                }
              }}
            />
          </CardHeader>
          <CardBody>
            {!seasonCalendar ? (
              <Spinner size="sm" color="primary" />
            ) : (
              <div className="d-flex flex-column gap-3">
                {seasonCalendar.packs.map((pack) => (
                  <div
                    key={pack.key}
                    className="d-flex flex-wrap gap-2 align-items-center justify-content-between border-bottom pb-2"
                  >
                    <div>
                      <div className="fw-semibold">
                        {t(`platformTemplates.tag.${pack.tag}`, {
                          defaultValue: pack.tag,
                        })}
                      </div>
                      <div className="text-muted small">
                        {pack.default_starts_on} → {pack.default_ends_on} ·{" "}
                        {t("sa.platformTemplates.season.models", {
                          count: pack.templates.length,
                        })}
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        color="primary"
                        size="sm"
                        disabled={
                          seasonBusyPack === pack.key ||
                          pack.templates.length === 0
                        }
                        onClick={() => void applySeasonPack(pack.key, false)}
                      >
                        {seasonBusyPack === pack.key ? (
                          <Spinner size="sm" />
                        ) : (
                          t("sa.platformTemplates.season.apply")
                        )}
                      </Button>
                      <Button
                        color="light"
                        size="sm"
                        disabled={
                          seasonBusyPack === pack.key ||
                          pack.templates.length === 0
                        }
                        onClick={() => void applySeasonPack(pack.key, true)}
                      >
                        {t("sa.platformTemplates.season.clear")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div className="d-flex flex-wrap gap-2 flex-grow-1 align-items-center">
                  <div style={{ maxWidth: 280, flex: "1 1 200px" }}>
                    <Input
                      type="search"
                      placeholder={t(
                        "sa.platformTemplates.search.placeholder"
                      )}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <Input
                    type="select"
                    style={{ maxWidth: 180 }}
                    value={activeFilter}
                    onChange={(e) => {
                      setPage(1);
                      setActiveFilter(e.target.value as ActiveFilter);
                    }}
                    aria-label={t("sa.platformTemplates.filter.status")}
                  >
                    <option value="">
                      {t("sa.platformTemplates.filter.statusAll")}
                    </option>
                    <option value="1">
                      {t("sa.platformTemplates.status.active")}
                    </option>
                    <option value="0">
                      {t("sa.platformTemplates.status.inactive")}
                    </option>
                  </Input>
                  {hasFilters && (
                    <Button
                      color="link"
                      className="text-decoration-underline p-0"
                      onClick={clearFilters}
                    >
                      {t("sa.platformTemplates.filter.clear")}
                    </Button>
                  )}
                </div>
                <Button
                  color="primary"
                  tag={Link}
                  to="/super-admin/platform-templates/new"
                >
                  {t("sa.platformTemplates.create")}
                </Button>
              </CardHeader>
              <CardBody>
                {canDrag && items.length > 1 && (
                  <p className="text-muted small mb-3">
                    {t("sa.platformTemplates.reorder.hint")}
                  </p>
                )}
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="avatar-lg mx-auto mb-3">
                      <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-1">
                        <i className="ri-layout-masonry-line" />
                      </div>
                    </div>
                    <h5 className="mb-2">
                      {hasFilters
                        ? t("sa.platformTemplates.empty.filteredTitle")
                        : t("sa.platformTemplates.empty.title")}
                    </h5>
                    <p className="text-muted mb-3">
                      {hasFilters
                        ? t("sa.platformTemplates.empty.filteredDescription")
                        : t("sa.platformTemplates.empty.description")}
                    </p>
                    {hasFilters ? (
                      <Button color="light" onClick={clearFilters}>
                        {t("sa.platformTemplates.filter.clear")}
                      </Button>
                    ) : (
                      <Button
                        color="primary"
                        tag={Link}
                        to="/super-admin/platform-templates/new"
                      >
                        {t("sa.platformTemplates.empty.cta")}
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <p className="text-muted mb-3">
                      {t("sa.platformTemplates.totalCount", { count: total })}
                      {reordering ? (
                        <Spinner size="sm" className="ms-2" />
                      ) : null}
                    </p>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            {canDrag && <th style={{ width: 40 }} />}
                            <th>{t("sa.platformTemplates.table.name")}</th>
                            <th>
                              {t("sa.platformTemplates.table.category")}
                            </th>
                            <th>
                              {t("sa.platformTemplates.table.description")}
                            </th>
                            <th>{t("sa.platformTemplates.table.clones")}</th>
                            <th>{t("sa.platformTemplates.table.status")}</th>
                            <th style={{ width: 200 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, index) => (
                            <tr
                              key={item.id}
                              draggable={canDrag && !reordering}
                              onDragStart={() => setDragIndex(index)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => onDropRow(index)}
                              style={{
                                opacity: dragIndex === index ? 0.5 : 1,
                                cursor: canDrag ? "grab" : undefined,
                              }}
                            >
                              {canDrag && (
                                <td className="text-muted">
                                  <i className="ri-draggable" />
                                </td>
                              )}
                              <td className="fw-medium">{item.name}</td>
                              <td>
                                <Badge color="light" className="text-body">
                                  {t(
                                    `platformTemplates.category.${item.category}`,
                                    { defaultValue: item.category }
                                  )}
                                </Badge>
                              </td>
                              <td
                                className="text-muted text-truncate"
                                style={{ maxWidth: 220 }}
                              >
                                {localizePlatformDescription(
                                  item.description,
                                  i18n.language
                                ) || "—"}
                              </td>
                              <td>{item.clone_count ?? 0}</td>
                              <td>
                                <Badge
                                  color={
                                    item.is_active ? "success" : "secondary"
                                  }
                                >
                                  {item.is_active
                                    ? t("sa.platformTemplates.status.active")
                                    : t(
                                        "sa.platformTemplates.status.inactive"
                                      )}
                                </Badge>
                              </td>
                              <td className="text-end">
                                <div className="d-flex gap-2 justify-content-end">
                                  <Button
                                    color="soft-primary"
                                    size="sm"
                                    tag={Link}
                                    to={`/super-admin/platform-templates/${item.id}/edit`}
                                  >
                                    {t("sa.platformTemplates.edit")}
                                  </Button>
                                  <Button
                                    color="soft-danger"
                                    size="sm"
                                    onClick={() => setDeleting(item)}
                                  >
                                    {t("sa.platformTemplates.delete.button")}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {lastPage > 1 && (
                      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mt-3">
                        <span className="text-muted small">
                          {t("sa.platformTemplates.pagination.label", {
                            page,
                            lastPage,
                            total,
                          })}
                        </span>
                        <div className="d-flex gap-2">
                          <Button
                            color="light"
                            size="sm"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            {t("sa.platformTemplates.pagination.prev")}
                          </Button>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page >= lastPage}
                            onClick={() =>
                              setPage((p) => Math.min(lastPage, p + 1))
                            }
                          >
                            {t("sa.platformTemplates.pagination.next")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={Boolean(deleting)} toggle={() => setDeleting(null)}>
          <ModalHeader toggle={() => setDeleting(null)}>
            {t("sa.platformTemplates.delete.title")}
          </ModalHeader>
          <ModalBody>
            <p className="mb-2">
              {t("sa.platformTemplates.delete.confirm", {
                name: deleting?.name || "",
              })}
            </p>
            <p className="text-muted small mb-0">
              {t("sa.platformTemplates.delete.consequence")}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setDeleting(null)}>
              {t("sa.platformTemplates.delete.cancel")}
            </Button>
            <Button
              color="danger"
              disabled={deletingBusy}
              onClick={confirmDelete}
            >
              {deletingBusy ? (
                <Spinner size="sm" />
              ) : (
                t("sa.platformTemplates.delete.confirmButton")
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default PlatformTemplatesList;
